import { supabase } from './supabase';

// =============================================================================
// Uwierzytelnianie dwuskładnikowe (2FA) — Supabase MFA, czynnik TOTP.
//
// Właściciel (13.09.2026): „dodaj w ustawieniach także uruchomienie
// zabezpieczenia 2FA — możesz do tego wykorzystać Supabase". Czynnik jest
// cechą KONTA (auth.mfa_factors), nie najemcy: jedna osoba ma jeden komplet
// czynników niezależnie od liczby organizacji, w których pracuje.
//
// Czyste rozstrzygnięcia (co zrobić z poziomem AAL, dokąd przekierować) są
// tu bez Reacta i bez Supabase, żeby dało się je zmierzyć wprost; wrappery
// klienta na dole tylko tłumaczą odpowiedzi auth-js na nasze kształty.
//
// Wymuszenie w powłoce (ProtectedRoute) to jedyny mechanizm; RLS nie wymaga
// `aal2` — świadomie, patrz SESJA_POWLOKI_I_2FA.md §3.5.
// =============================================================================

export type PoziomAal = 'aal1' | 'aal2';

/** Co sesja mówi o 2FA — z JWT (`getAuthenticatorAssuranceLevel`), bez sieci. */
export interface StanMfa {
  /** Poziom bieżącej sesji. */
  poziom: PoziomAal;
  /** Poziom, do którego sesja MOŻE dojść (aal2 = konto ma zweryfikowany czynnik). */
  nastepny: PoziomAal;
}

export const BRAK_STANU_MFA: StanMfa = { poziom: 'aal1', nastepny: 'aal1' };

/** Czy konto ma zweryfikowany czynnik (niezależnie od tego, czy ta sesja go potwierdziła). */
export function maCzynnik(stan: StanMfa): boolean {
  return stan.nastepny === 'aal2';
}

/** Czy TA sesja musi jeszcze podać kod, zanim zobaczy panel. */
export function wymagaKodu(stan: StanMfa): boolean {
  return stan.nastepny === 'aal2' && stan.poziom === 'aal1';
}

/** Ekran kodu 2FA po logowaniu. Poza ProtectedRoute, bo sam jest bramką. */
export const SCIEZKA_KODU_2FA = '/2fa';

/** Parametr, z którym strona ustawień super-admina otwiera się z wymuszeniem. */
export const PARAM_WYMUSZENIA = 'dwuskladnikowe';
export const WARTOSC_WYMUSZENIA = 'wymagane';
export const SCIEZKA_USTAWIEN_SUPER_ADMINA = '/super-admin/settings';

export interface WejscieTrasy {
  stan: StanMfa;
  /** `location.pathname` trasy, którą użytkownik chce zobaczyć. */
  sciezka: string;
  /** Czy trasa jest panelem super-admina (`requiredPanel === 'super-admin'`). */
  panelSuperAdmina: boolean;
  /** Flaga `require2faSuperAdmin` z `system_settings.global`. */
  wymuszoneDlaSuperAdminow: boolean;
}

/**
 * Dokąd przekierować PRZED wpuszczeniem na trasę chronioną; `null` = wpuścić.
 *
 * Kolejność ma znaczenie: sesja z czynnikiem, która nie podała kodu, idzie
 * na `/2fa` ZAWSZE (także do ustawień), bo inaczej ustawienia byłyby drogą
 * obejścia kodu. Wymuszenie włączenia dla super-adminów dotyczy wyłącznie kont
 * bez czynnika i przepuszcza stronę ustawień, na której czynnik się włącza —
 * inaczej nie dałoby się spełnić wymogu.
 */
export function dokadPrzedTrasa(w: WejscieTrasy): string | null {
  if (wymagaKodu(w.stan)) return SCIEZKA_KODU_2FA;
  if (w.panelSuperAdmina && w.wymuszoneDlaSuperAdminow && !maCzynnik(w.stan)) {
    if (w.sciezka === SCIEZKA_USTAWIEN_SUPER_ADMINA) return null;
    return `${SCIEZKA_USTAWIEN_SUPER_ADMINA}?${PARAM_WYMUSZENIA}=${WARTOSC_WYMUSZENIA}`;
  }
  return null;
}

/** Kod TOTP: sześć cyfr; użytkownik wkleja ze spacją, więc spacje wycinamy. */
export function znormalizujKod(surowy: string): string | null {
  const kod = surowy.replace(/\s+/g, '');
  return /^\d{6}$/.test(kod) ? kod : null;
}

// -----------------------------------------------------------------------------
// Wrappery klienta
// -----------------------------------------------------------------------------

export interface CzynnikTotp {
  id: string;
  nazwa: string;
  status: 'verified' | 'unverified';
  utworzony: string;
}

export interface RozpoczeteWlaczanie {
  factorId: string;
  /** SVG jako data-URL — auth-js oddaje gotowy `qr_code`. */
  qr: string;
  /** Sekret do wpisania ręcznie, gdy aparat nie czyta kodu. */
  sekret: string;
}

/** Przyjazna nazwa czynnika; GoTrue wymaga unikalnej per konto. */
export const NAZWA_CZYNNIKA = 'Aplikacja uwierzytelniająca';

interface OdpowiedzZBledem<T> {
  data: T | null;
  error: { message: string; code?: string } | null;
}

/** Podzbiór `supabase.auth.mfa`, którego używamy — do podmiany w testach. */
export interface KlientMfa {
  getAuthenticatorAssuranceLevel: () => Promise<
    OdpowiedzZBledem<{ currentLevel: string | null; nextLevel: string | null }>
  >;
  listFactors: () => Promise<
    OdpowiedzZBledem<{
      totp: Array<{ id: string; friendly_name?: string; status: string; created_at: string }>;
    }>
  >;
  enroll: (p: {
    factorType: 'totp';
    friendlyName: string;
  }) => Promise<OdpowiedzZBledem<{ id: string; totp: { qr_code: string; secret: string } }>>;
  challengeAndVerify: (p: { factorId: string; code: string }) => Promise<OdpowiedzZBledem<unknown>>;
  unenroll: (p: { factorId: string }) => Promise<OdpowiedzZBledem<unknown>>;
}

const klientDomyslny = (): KlientMfa => supabase.auth.mfa as unknown as KlientMfa;

function rzuc(error: { message: string; code?: string } | null, domyslne: string): never {
  const e = new Error(error?.message || domyslne) as Error & { code?: string };
  e.code = error?.code;
  throw e;
}

/** Stan z JWT bieżącej sesji — bez żądania sieciowego. */
export async function pobierzStanMfa(mfa: KlientMfa = klientDomyslny()): Promise<StanMfa> {
  const { data, error } = await mfa.getAuthenticatorAssuranceLevel();
  if (error || !data) return BRAK_STANU_MFA;
  return {
    poziom: data.currentLevel === 'aal2' ? 'aal2' : 'aal1',
    nastepny: data.nextLevel === 'aal2' ? 'aal2' : 'aal1',
  };
}

export async function pobierzCzynniki(mfa: KlientMfa = klientDomyslny()): Promise<CzynnikTotp[]> {
  const { data, error } = await mfa.listFactors();
  if (error) rzuc(error, 'Nie udało się odczytać czynników 2FA.');
  return (data?.totp ?? []).map((f) => ({
    id: f.id,
    nazwa: f.friendly_name || NAZWA_CZYNNIKA,
    status: f.status === 'verified' ? 'verified' : 'unverified',
    utworzony: f.created_at,
  }));
}

/**
 * Rozpoczyna włączanie: nowy czynnik `unverified` + kod QR. Porzucone
 * poprzednie próby (czynnik `unverified` o tej samej nazwie) sprząta najpierw,
 * bo GoTrue odmawia drugiego czynnika o tej samej nazwie
 * (`mfa_factor_name_conflict`) i człowiek utknąłby po jednym zamkniętym oknie.
 */
export async function rozpocznijWlaczanie(
  mfa: KlientMfa = klientDomyslny()
): Promise<RozpoczeteWlaczanie> {
  const czynniki = await pobierzCzynniki(mfa);
  for (const c of czynniki) {
    if (c.status === 'unverified') {
      const { error } = await mfa.unenroll({ factorId: c.id });
      if (error) rzuc(error, 'Nie udało się usunąć porzuconej próby włączenia 2FA.');
    }
  }
  const { data, error } = await mfa.enroll({ factorType: 'totp', friendlyName: NAZWA_CZYNNIKA });
  if (error || !data) rzuc(error, 'Nie udało się rozpocząć włączania 2FA.');
  return { factorId: data.id, qr: data.totp.qr_code, sekret: data.totp.secret };
}

const ZLY_KOD = 'Kod nie zgadza się — sprawdź zegar telefonu i spróbuj ponownie.';
const POWTORZ_POTWIERDZ = 'naciśnij „Potwierdź" jeszcze raz — powstanie nowe wyzwanie.';

/**
 * Kody GoTrue z `verify`, które mają własne polskie zdanie; inne idą do karty
 * surowo. Dwa ostatnie odpowiadają na jedno pytanie — „czy wyzwanie jeszcze
 * obowiązuje": `mfa_challenge_expired` po kilku minutach z otwartym ekranem,
 * `mfa_ip_address_mismatch`, gdy wyzwanie i kod przyszły z różnych adresów
 * (zmierzone na produkcji 14.09.2026 sondą za proxy o zmiennym wyjściu;
 * u człowieka to przełączenie Wi‑Fi ↔ sieć komórkowa w trakcie wpisywania).
 * `challengeAndVerify` zakłada nowe wyzwanie przy każdym wywołaniu, więc
 * zdanie mówi, co zrobić, zamiast cytować GoTrue.
 */
const ZDANIA_BLEDOW_WERYFIKACJI: Record<string, string> = {
  mfa_verification_failed: ZLY_KOD,
  mfa_verification_rejected: ZLY_KOD,
  mfa_challenge_expired: `Wyzwanie wygasło — ${POWTORZ_POTWIERDZ}`,
  mfa_ip_address_mismatch: `Zmienił się adres sieci w trakcie wpisywania kodu — ${POWTORZ_POTWIERDZ}`,
};

/** Kod z aplikacji potwierdza czynnik i od razu podnosi TĘ sesję do aal2. */
export async function potwierdzKod(
  factorId: string,
  surowyKod: string,
  mfa: KlientMfa = klientDomyslny()
): Promise<void> {
  const kod = znormalizujKod(surowyKod);
  if (!kod) throw new Error('Kod ma sześć cyfr.');
  const { error } = await mfa.challengeAndVerify({ factorId, code: kod });
  if (error) {
    const zdanie = error.code ? ZDANIA_BLEDOW_WERYFIKACJI[error.code] : undefined;
    rzuc(zdanie ? { ...error, message: zdanie } : error, 'Nie udało się potwierdzić kodu.');
  }
}

/**
 * Wyłącza czynnik. GoTrue wymaga sesji aal2 do usunięcia zweryfikowanego
 * czynnika, więc gdy sesja jest aal1, kod jest OBOWIĄZKOWY i najpierw podnosi
 * sesję — to jest zabezpieczenie: kto zna hasło, ale nie ma telefonu, nie
 * wyłączy 2FA z zalogowanej karty.
 */
export async function wylaczCzynnik(
  factorId: string,
  stan: StanMfa,
  surowyKod: string,
  mfa: KlientMfa = klientDomyslny()
): Promise<void> {
  if (stan.poziom !== 'aal2') await potwierdzKod(factorId, surowyKod, mfa);
  const { error } = await mfa.unenroll({ factorId });
  if (error) rzuc(error, 'Nie udało się wyłączyć 2FA.');
}
